import React from 'react';

import { mdiDotsHorizontal } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, Menu, MenuItem, TableRow } from '@mui/material';
import axios from 'axios';
import copy from 'copy-to-clipboard';
import _ from 'lodash';
import Moment from 'moment';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassSessionHeatmapUserRow from './ClassSessionHeatmapUserRow';
import { GET_PARTICIPANT_LOGS } from '../../../../../config/apiPaths';
import { localStorageKeys } from '../../../../../localStorageKeys';
import { getUrls } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';
import { CloudWatchLogProvider } from '../../../CloudWatchLogContext';
import ModalContainer from '../../../ModalContainer';
import { ActionsTableCell, StyledTableCell } from '../../../styles/table-styles';
import { CloudWatchLogsView } from '../../CloudWatchLogsView';

const StyledTableRow = styled(TableRow)`
    padding: 2rem;
`;

const MenuIcon = styled(Icon)`
    max-width: 35px;
    color: ${(props) => props.theme.colors.primary.main};
    height: 32px;
`;

const ClassSessionUserRow = ({ classSessionUser, classSessionData }) => {
    const { t } = useTranslation('portal');
    console.log('classSessionData', classSessionData);
    console.log('classSessionUser', classSessionUser);
    const search = new URLSearchParams(window.location.search);
    const userUuid = search.get('user_uuid') || window.localStorage.getItem(localStorageKeys.userUuid);
    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);

    const { pushModalContent, popModalContent } = useContainer(ModalContainer);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { api } = getUrls();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleGetLogs = (id) => {
        console.log('handleGetLogs', id);
        const participantId = classSessionUser.meeting_zoom_id.split('_')[2];
        pushModalContent(
            <CloudWatchLogProvider
                style={{ height: '600px', maxHeight: '600px', overflowY: 'auto' }}
                primaryQuery={`participant_id:${participantId}`}
                secondaryQuery={''}
                startTime={Number(classSessionData.start_time) - 1000 * 60 * 5}
                endTime={Number(classSessionData.end_time) + 1000 * 60 * 5}
                addSearchBox={true}
            >
                <CloudWatchLogsView />
            </CloudWatchLogProvider>
        );
        handleClose();
    };

    const handleGetActivities = (id) => {
        console.log('handleGetActivities', id);
        pushModalContent(
            <Box
                style={{
                    width: `${window.innerWidth - 200}px`
                }}
            >
                <ClassSessionHeatmapUserRow
                    classSessionData={classSessionData}
                    student={classSessionUser}
                    startShown={true}
                ></ClassSessionHeatmapUserRow>
            </Box>
        );
        handleClose();
    };

    const handleGetClientLogs = async (id) => {
        console.log('handleGetClientLogs', id);
        handleClose();
        const loadingStr = 'loading';
        pushModalContent(
            <Box
                style={{
                    width: `${window.innerWidth - 200}px`
                }}
            >
                <pre>{loadingStr}</pre>
            </Box>
        );
        const participantId = id.split('_')[2];
        const {
            data: { urls }
        } = await axios.get(
            `${api}${GET_PARTICIPANT_LOGS}?mode=urls&user_uuid=${userUuid}&class_session_uuid=${classSessionData.class_session_uuid}&participant_id=${participantId}&auth_mode=${authMode}&demo_content=${showDemoContent}`
        );

        let urlResults: any[] = [];
        const logsAr: string[] = [];
        await Promise.all(
            urls.map(async (url) => {
                const { data } = await axios.get(url);
                urlResults.push({ url, data });
            })
        );
        urlResults = _.sortBy(urlResults, 'url');
        urlResults.forEach((urlResult) => {
            urlResult.data.split('\n').forEach((log) => {
                if (log && log.trim() !== '') {
                    logsAr.push(log);
                }
            });
        });

        popModalContent();
        pushModalContent(
            <Box
                style={{
                    width: `${window.innerWidth - 200}px`
                }}
            >
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        copy(logsAr.join('\n'));
                    }}
                >
                    {t('copy-logs')}
                </Button>

                <pre
                    style={{
                        overflow: 'scroll',
                        fontSize: '12px',
                        lineHeight: '16px',
                        maxHeight: `${window.innerHeight - 300}px`
                    }}
                >
                    {logsAr.map((log) => {
                        let color = 'rgb(255,255,255)';
                        const type = log.split(':')[3];
                        if (type && type === 'WARN') {
                            color = 'rgb(255,255,200)';
                        } else if (type && type === 'ERROR') {
                            color = 'rgb(255,200,200)';
                        } else if (type && type === 'INFO') {
                            color = 'rgb(200,255,200)';
                        } else if (type && type === 'DEBUG') {
                            color = 'rgb(200,200,255)';
                        }

                        return <div style={{ backgroundColor: color }}>{log}</div>;
                    })}
                </pre>
            </Box>
        );
    };

    return (
        <StyledTableRow>
            <StyledTableCell>{classSessionUser.meeting_zoom_id}</StyledTableCell>
            <StyledTableCell>{classSessionUser.user_name}</StyledTableCell>
            <StyledTableCell>{Moment(new Date(Number(classSessionUser.join_time))).format('h:mm A')}</StyledTableCell>
            <StyledTableCell>
                {Moment(new Date(Number(classSessionUser.history_timestamp))).format('h:mm A')}
            </StyledTableCell>
            <StyledTableCell>{classSessionUser.breakout_id || t('main-room')}</StyledTableCell>
            <StyledTableCell>{classSessionUser.device.system}</StyledTableCell>
            <StyledTableCell>{classSessionUser.device.version}</StyledTableCell>
            <ActionsTableCell>
                <Button id="zoom-state-actions" size={'small'} onClick={handleClick}>
                    <MenuIcon path={mdiDotsHorizontal} />
                </Button>
            </ActionsTableCell>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{ 'aria-labelledby': 'student-action-button' }}
            >
                <MenuItem onClick={() => handleGetLogs(classSessionUser.meeting_zoom_id)}>{t('view-logs')}</MenuItem>
                <MenuItem onClick={() => handleGetClientLogs(classSessionUser.meeting_zoom_id)}>
                    {t('view-client-logs')}
                </MenuItem>
                <MenuItem onClick={() => handleGetActivities(classSessionUser.meeting_zoom_id)}>
                    {t('view-activities')}
                </MenuItem>
            </Menu>
        </StyledTableRow>
    );
};

export default ClassSessionUserRow;
